import {
  createSettingMetadataFromGroupDefaults,
  type SettingMetadataDefaultsContext,
} from "../../settings-catalog/metadata";
import type {
  SettingAgentEditability,
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  CustomerWidgetFlatTeamSettingGroupById,
  type CustomerWidgetFlatTeamSettingGroupId,
} from "./groups";

interface CustomerWidgetSettingInput extends SettingRegistryMetadataInput {}

interface CustomerWidgetSettingRegistryEntry extends CustomerWidgetSettingInput {
  group: CustomerWidgetFlatTeamSettingGroupId;
}

function setting(
  metadata: CustomerWidgetSettingInput,
): CustomerWidgetSettingInput {
  return metadata;
}

function customerWidgetGroup<
  const Group extends CustomerWidgetFlatTeamSettingGroupId,
  const T extends Record<string, CustomerWidgetSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const CustomerWidgetFlatSettingCatalogRegistry = {
  ...customerWidgetGroup("customer_widget.appearance", {
    "customerWidget/general/widgetColor": setting({
      label: "Widget color",
      description:
        "Primary brand color used by the storefront customer widget launcher, header, and prominent actions.",
      synonyms: [
        "chat widget color",
        "chat widget colors",
        "colors chat widget",
        "customer widget color",
        "storefront widget color",
        "launcher color",
        "primary widget color",
        "make widget red",
        "red widget",
        "red",
      ],
      valueSemantics: { defaultValue: "#181818", inputKind: "color" },
    }),
    "customerWidget/general/widgetSecondaryColorEnabled": setting({
      label: "Secondary color enabled",
      description: "Allows the customer widget to use the secondary color.",
      synonyms: [
        "secondary widget color",
        "accent widget color",
        "enable secondary color",
        "enable accent color",
        "color options",
      ],
      dependencies: [
        {
          key: "customerWidget/general/widgetSecondaryColor",
          relationship: "controls",
          description: "Controls whether the secondary widget color is used.",
        },
      ],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Off",
        trueLabel: "On",
      },
    }),
    "customerWidget/general/widgetSecondaryColor": setting({
      label: "Widget secondary color",
      description:
        "Secondary brand color used by the storefront customer widget when secondary color is enabled.",
      synonyms: [
        "secondary widget color",
        "accent widget color",
        "chat widget accent color",
      ],
      dependencies: [
        {
          key: "customerWidget/general/widgetSecondaryColorEnabled",
          relationship: "requires",
          description: "Requires secondary color to be enabled.",
        },
      ],
      valueSemantics: { defaultValue: "#666666", inputKind: "color" },
    }),
    "customerWidget/general/widgetPosition": setting({
      label: "Widget position",
      description: "Launcher position on the storefront.",
      synonyms: ["launcher position", "widget placement", "chat placement"],
      valueSemantics: {
        defaultValue: "right",
        options: [
          { value: "left", label: "Left" },
          { value: "right", label: "Right" },
        ],
      },
    }),
    "customerWidget/general/headingLogoUrl": setting({
      label: "Heading logo URL",
      description: "Logo image shown in the customer widget header.",
      synonyms: ["widget logo", "chat widget icon", "header logo"],
      valueSemantics: { inputKind: "url" },
    }),
    "customerWidget/general/mainHeadingText": setting({
      label: "Main heading text",
      description: "Primary heading shown on the customer widget home screen.",
      synonyms: ["welcome text", "greeting text", "widget greeting"],
      valueSemantics: { defaultValue: "Hey!" },
    }),
    "customerWidget/general/homeHeadingText": setting({
      label: "Home heading text",
      description:
        "Secondary heading shown on the customer widget home screen.",
      synonyms: ["home greeting", "welcome heading"],
      valueSemantics: { defaultValue: "What can we do for you today?" },
    }),
    "customerWidget/general/bubbleButtonText": setting({
      label: "Bubble button text",
      description: "Text shown on the collapsed widget launcher bubble.",
      synonyms: ["launcher text", "chat bubble text"],
    }),
    "customerWidget/general/chatWithUsButtonText": setting({
      label: "Chat button text",
      description: "Text shown on the chat call-to-action button.",
      synonyms: ["chat with us text", "chat button label"],
      valueSemantics: { defaultValue: "Ask anything..." },
    }),
    "customerWidget/general/widgetButtonFontSize": setting({
      label: "Button font size",
      description: "Font size for customer widget buttons.",
      valueSemantics: { defaultValue: 14, unit: "px" },
    }),
    "customerWidget/general/widgetHeaderFontSize": setting({
      label: "Header font size",
      description: "Font size for customer widget header text.",
      valueSemantics: { defaultValue: 30, unit: "px" },
    }),
    "customerWidget/general/widgetMessageBubbleFontSize": setting({
      label: "Message bubble font size",
      description: "Font size for message bubbles inside the customer widget.",
      valueSemantics: { defaultValue: 12, unit: "px" },
    }),
    "customerWidget/general/widgetFontFamily": setting({
      label: "Widget font family",
      description: "Font family used by the customer widget.",
    }),
    "customerWidget/general/openToWelcomePage": setting({
      label: "Open to welcome page",
      description: "Opens the customer widget to the welcome page by default.",
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Open to default page",
        trueLabel: "Open to welcome page",
      },
    }),
    "customerWidget/general/mobileFullscreen": setting({
      label: "Mobile fullscreen",
      description: "Shows the customer widget fullscreen on mobile devices.",
      valueSemantics: {
        defaultValue: true,
        falseLabel: "Embedded on mobile",
        trueLabel: "Fullscreen on mobile",
      },
    }),
  }),
};

export type CustomerWidgetFlatTeamSettingKey =
  keyof typeof CustomerWidgetFlatSettingCatalogRegistry;

const CustomerWidgetFlatTeamSettingKeys = Object.keys(
  CustomerWidgetFlatSettingCatalogRegistry,
) as CustomerWidgetFlatTeamSettingKey[];

const CustomerWidgetFlatTeamSettingKeySet = new Set<string>(
  CustomerWidgetFlatTeamSettingKeys,
);

export function isCustomerWidgetFlatTeamSettingKey(
  key: string,
): key is CustomerWidgetFlatTeamSettingKey {
  return CustomerWidgetFlatTeamSettingKeySet.has(key);
}

export const CustomerWidgetFlatTeamSettingCatalogDefinitions =
  CustomerWidgetFlatTeamSettingKeys.map((key) => {
    const definition = CustomerWidgetFlatSettingCatalogRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: CustomerWidgetFlatTeamSettingGroupById[definition.group],
        resolveAgentEditability: createCustomerWidgetSettingAgentEditability,
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    CustomerWidgetFlatTeamSettingKey,
    CustomerWidgetFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<CustomerWidgetFlatTeamSettingKey>
  >[];

export const CustomerWidgetFlatTeamSettingMetadataByKey = Object.fromEntries(
  CustomerWidgetFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<CustomerWidgetFlatTeamSettingKey, SettingMetadata>;

function createCustomerWidgetSettingAgentEditability({
  setting,
  group,
  access,
  risk,
  affectsBilling,
}: SettingMetadataDefaultsContext<CustomerWidgetSettingRegistryEntry>): SettingAgentEditability {
  if (setting.agentEditability) {
    return setting.agentEditability;
  }

  if (access.write.mode === "none") {
    return { mode: "never", reason: access.write.reason };
  }

  if (affectsBilling || risk.level === "high" || risk.level === "critical") {
    return {
      mode: "suggestOnly",
      reason: "High-risk settings should be reviewed before saving.",
    };
  }

  return group.agentEditability;
}
